import styled from "@emotion/styled";
import {
  HistoryEntryTypeEnum,
  createHistoryEntry,
  useProfile,
} from "@repo/api";
import { Button, FormField, TextArea } from "@repo/skapa";
import { t } from "@repo/translation";
import { FlexDiv, useToast } from "@repo/ui";
import { getErrorMessage, trackAnalytics } from "@repo/utils";
import { Controller, useForm, useWatch } from "react-hook-form";

import { MAX_NOTE_LENGTH, MIN_NOTE_LENGTH } from "./noteUtils";

const CompactFormField = styled(FormField)`
  && {
    margin-bottom: 0px;
  }
`;

interface InlineNoteInputProps {
  opportunityId: number;
}

interface NoteFormValues {
  note: string;
}

export const InlineNoteInput = ({ opportunityId }: InlineNoteInputProps) => {
  const { data: profile } = useProfile();
  const { updateToast } = useToast();

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<NoteFormValues>({
    defaultValues: { note: "" },
    mode: "onChange",
  });

  const noteValue = useWatch({ control, name: "note" });

  const onSubmit = ({ note }: NoteFormValues) => {
    return createHistoryEntry(
      {
        entryType: HistoryEntryTypeEnum.CUSTOM_NOTES,
        note,
        createdBy: profile?.mail ?? "",
      },
      opportunityId
    )
      .then(() => {
        reset();
        updateToast({ title: t("prompt.note_saved") });
        trackAnalytics("add_custom_note");
      })
      .catch((err) => {
        updateToast({
          title: t("prompt.note_saved_failed"),
          message: getErrorMessage(err),
          type: "error",
        });
      });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Controller
        name="note"
        control={control}
        rules={{
          required: true,
          minLength: MIN_NOTE_LENGTH,
          maxLength: MAX_NOTE_LENGTH,
        }}
        render={({ field, fieldState }) => (
          <>
            <CompactFormField
              fieldHelper={{
                id: "helper-msg-id",
                msg: `${noteValue.length}/${MAX_NOTE_LENGTH}`,
              }}
              shouldValidate={fieldState.isDirty && Boolean(fieldState.error)}
              valid={fieldState.isDirty && !fieldState.error}
              validation={
                fieldState.error
                  ? {
                      id: "inline-note-error",
                      msg: t("prompt.note_too_short"),
                      type: "error",
                    }
                  : undefined
              }
            >
              <TextArea
                id="inline-note-input"
                label={t("label.write_a_note")}
                maxLength={MAX_NOTE_LENGTH}
                {...field}
              />
            </CompactFormField>
            <FlexDiv justifyContent="end" alignItems="flex-end">
              <Button
                size="xsmall"
                text={t("action.add_note")}
                type="primary"
                htmlType="submit"
                loading={isSubmitting}
                disabled={noteValue.length <= 1}
              />
            </FlexDiv>
          </>
        )}
      />
    </form>
  );
};
